// src/lib/gap-analyzer.ts
// Analyzes gaps between requirements and evidence to produce actionable recommendations

import {
  ContractScoredLibrary,
  MatchingResult,
  GapReport,
  CriticalGap,
  ImprovementOpportunity,
  ProposalStrength,
  ProfileCompleteness,
  WinAssessment,
  ImprovementRoadmap,
  ImprovementAction
} from '../types/source-grounded';

import { RelevanceTier } from './taxonomy/types';

/**
 * Gap Analyzer
 * Produces comprehensive gap reports and improvement recommendations
 */
export class GapAnalyzer {
  /**
   * Analyze gaps and generate a comprehensive report
   */
  analyzeGaps(
    matchingResult: MatchingResult,
    library: ContractScoredLibrary
  ): GapReport {
    console.log(`📊 Analyzing gaps for ${matchingResult.contractTitle}`);

    // Identify critical gaps
    const criticalGaps = this.identifyCriticalGaps(matchingResult);

    // Identify improvement opportunities
    const improvementOpportunities = this.identifyImprovementOpportunities(matchingResult);

    // Identify strengths to highlight
    const strengthsToHighlight = this.identifyStrengths(library, matchingResult);

    // Assess profile completeness
    const profileCompleteness = this.assessProfileCompleteness(library);

    // Calculate win probability
    const winProbability = this.calculateWinProbability(
      matchingResult,
      criticalGaps.length,
      library.summary.highRelevanceCount
    );

    // Determine competitive position
    const competitivePosition = this.determineCompetitivePosition(
      matchingResult.overallReadiness,
      criticalGaps.length,
      strengthsToHighlight.length
    );

    const report: GapReport = {
      overallReadiness: matchingResult.overallReadiness,
      winProbabilityEstimate: winProbability,
      competitivePosition,
      criticalGaps,
      improvementOpportunities,
      strengthsToHighlight,
      profileCompleteness,
      summary: {
        totalRequirements: matchingResult.totalRequirements,
        fullyMet: matchingResult.fullMatches,
        partiallyMet: matchingResult.partialMatches,
        notMet: matchingResult.noMatches,
        criticalGapsCount: criticalGaps.length,
        recommendationsCount: (matchingResult?.recommendations || []).length
      }
    };

    console.log(`✅ Gap analysis complete:`);
    console.log(`   - Win probability: ${winProbability}%`);
    console.log(`   - Competitive position: ${competitivePosition}`);
    console.log(`   - Critical gaps: ${criticalGaps.length}`);
    console.log(`   - Strengths: ${strengthsToHighlight.length}`);

    return report;
  }

  /**
   * Generate win assessment
   */
  generateWinAssessment(
    matchingResult: MatchingResult,
    library: ContractScoredLibrary,
    gapReport: GapReport
  ): WinAssessment {
    // Calculate factor scores
    const requirementCoverage = matchingResult.overallReadiness;

    const evidenceStrength = library.scoredEvidence.length > 0
      ? Math.round(
          library.scoredEvidence.reduce((sum, e) => sum + e.relevance.overallScore, 0) /
          library.scoredEvidence.length * 100
        )
      : 0;

    const relevanceAlignment = Math.round(
      ((library.summary.highRelevanceCount * 3) +
       (library.summary.mediumRelevanceCount * 2) +
       (library.summary.lowRelevanceCount * 1)) /
      (library.totalEvidenceCount * 3) * 100
    );

    const gapSeverity = Math.max(0, 100 - ((gapReport?.criticalGaps || []).length * 20) - ((gapReport?.improvementOpportunities || []).length * 5));

    // Identify strength and weakness areas
    const strengthAreas = this.identifyStrengthAreas(matchingResult, library);
    const weaknessAreas = this.identifyWeaknessAreas(matchingResult, gapReport);

    return {
      overallReadiness: gapReport.overallReadiness,
      winProbabilityEstimate: gapReport.winProbabilityEstimate,
      strengthAreas,
      weaknessAreas,
      competitivePosition: gapReport.competitivePosition,
      factors: {
        requirementCoverage,
        evidenceStrength,
        relevanceAlignment,
        gapSeverity
      }
    };
  }

  /**
   * Generate improvement roadmap
   */
  generateImprovementRoadmap(
    gapReport: GapReport,
    matchingResult: MatchingResult
  ): ImprovementRoadmap {
    const immediateActions: ImprovementAction[] = [];
    const shortTermActions: ImprovementAction[] = [];
    const longTermActions: ImprovementAction[] = [];

    // Critical gaps → Immediate actions
    for (const gap of (gapReport?.criticalGaps || [])) {
      immediateActions.push({
        action: gap.recommendation,
        impact: gap.impact,
        effort: 'HIGH',
        profileSection: gap.profileAction.split('→')[1]?.trim() || 'Profile'
      });
    }

    // HIGH priority recommendations → Immediate
    // MEDIUM priority → Short term
    // LOW priority → Long term
    for (const rec of (matchingResult?.recommendations || [])) {
      const action: ImprovementAction = {
        action: rec.action,
        impact: rec.impact,
        effort: rec.effort,
        profileSection: rec.profileSection
      };

      if (rec.priority === 'HIGH') {
        if (immediateActions.length < 5) {
          immediateActions.push(action);
        } else {
          shortTermActions.push({ ...action, timeframe: '1-2 weeks' });
        }
      } else if (rec.priority === 'MEDIUM') {
        shortTermActions.push({ ...action, timeframe: '1-2 weeks' });
      } else {
        longTermActions.push({ ...action, timeframe: 'Before next opportunity' });
      }
    }

    // Add improvement opportunities
    for (const opp of (gapReport?.improvementOpportunities || [])) {
      shortTermActions.push({
        action: opp.recommendation,
        impact: opp.impactOnWinProbability,
        effort: 'MEDIUM',
        profileSection: opp.profileAction.split('→')[1]?.trim() || 'Profile',
        timeframe: '1-2 weeks'
      });
    }

    // Deduplicate and limit
    return {
      immediateActions: this.deduplicateActions(immediateActions).slice(0, 5),
      shortTermActions: this.deduplicateActions(shortTermActions).slice(0, 10),
      longTermActions: this.deduplicateActions(longTermActions).slice(0, 5)
    };
  }

  /**
   * Identify critical gaps that may cause disqualification
   */
  private identifyCriticalGaps(matchingResult: MatchingResult): CriticalGap[] {
    const criticalGaps: CriticalGap[] = [];

    for (const match of (matchingResult?.matches || [])) {
      // Check for critical gaps in the match
      for (const gap of (match?.gaps || [])) {
        if (gap.severity === 'CRITICAL') {
          criticalGaps.push({
            requirement: match.requirementText,
            requirementSource: match.requirementSource,
            impact: gap.impact,
            recommendation: `Address: ${gap.description}`,
            profileAction: this.generateProfileAction(match.requirementCategory, gap.description)
          });
        }
      }

      // Also flag NONE matches as critical if requirement is mandatory
      if (match.matchStatus === 'NONE' && match.gaps.some(g => g.severity === 'CRITICAL')) {
        // Already captured above
      }
    }

    return criticalGaps;
  }

  /**
   * Identify opportunities to improve (not critical, but beneficial)
   */
  private identifyImprovementOpportunities(matchingResult: MatchingResult): ImprovementOpportunity[] {
    const opportunities: ImprovementOpportunity[] = [];

    for (const match of (matchingResult?.matches || [])) {
      // Partial matches are opportunities
      if (match.matchStatus === 'PARTIAL') {
        opportunities.push({
          requirement: match.requirementText.substring(0, 100) + '...',
          currentState: `Partial evidence (${match.matchedEvidence.length} items, confidence: ${Math.round(match.confidenceScore * 100)}%)`,
          recommendation: 'Add stronger direct evidence to improve match',
          impactOnWinProbability: 'Could improve evaluation score by 5-10%',
          profileAction: this.generateProfileAction(match.requirementCategory, 'Add supporting evidence')
        });
      }

      // Moderate gaps are opportunities
      for (const gap of (match?.gaps || [])) {
        if (gap.severity === 'MODERATE') {
          opportunities.push({
            requirement: match.requirementText.substring(0, 100) + '...',
            currentState: gap.description,
            recommendation: `Strengthen: ${gap.description}`,
            impactOnWinProbability: gap.impact,
            profileAction: this.generateProfileAction(match.requirementCategory, gap.description)
          });
        }
      }
    }

    return opportunities.slice(0, 10); // Limit to top 10
  }

  /**
   * Identify strengths to highlight in the proposal
   */
  private identifyStrengths(
    library: ContractScoredLibrary,
    matchingResult: MatchingResult
  ): ProposalStrength[] {
    const strengths: ProposalStrength[] = [];

    // High relevance evidence items are strengths
    for (const evidence of (library.evidenceByTier?.HIGH || [])) {
      strengths.push({
        evidenceId: evidence.evidenceId,
        strength: evidence.contentSummary,
        suggestedUsage: `Highlight in ${evidence.sourceType === 'past_performance' ? 'Past Performance' : 'Technical'} section`,
        relevance: 'HIGH'
      });
    }

    // Full matches indicate strong areas
    for (const match of (matchingResult?.matches || [])) {
      if (match.matchStatus === 'FULL' && match.matchedEvidence && match.matchedEvidence.length > 0) {
        const topEvidence = match.matchedEvidence[0];
        if (!strengths.some(s => s.evidenceId === topEvidence.evidenceId)) {
          strengths.push({
            evidenceId: topEvidence.evidenceId,
            strength: `Strong match for: ${match.requirementCategory}`,
            suggestedUsage: `Use as primary evidence for ${match.requirementSource}`,
            relevance: 'HIGH'
          });
        }
      }
    }

    // Medium relevance with many inferred capabilities
    for (const evidence of (library.evidenceByTier?.MEDIUM || [])) {
      if ((evidence.inferredCapabilities || []).length >= 3) {
        strengths.push({
          evidenceId: evidence.evidenceId,
          strength: `Demonstrates multiple capabilities: ${(evidence.inferredCapabilities || []).slice(0, 3).map(i => i.capabilityName).join(', ')}`,
          suggestedUsage: 'Reference across multiple sections',
          relevance: 'MEDIUM'
        });
      }
    }

    return strengths.slice(0, 15); // Limit to top 15
  }

  /**
   * Assess profile completeness by section
   */
  private assessProfileCompleteness(library: ContractScoredLibrary): ProfileCompleteness[] {
    const completeness: ProfileCompleteness[] = [];

    // Past Performance
    const ppCount = library.evidenceByType.past_performance.length;
    completeness.push({
      section: 'Past Performance',
      completeness: Math.min(100, ppCount * 20), // 5 = 100%
      missingElements: ppCount < 3
        ? ['Add more past performance records (recommended: 3-5)']
        : []
    });

    // Personnel
    const personnelCount = library.evidenceByType.personnel.length;
    completeness.push({
      section: 'Key Personnel',
      completeness: Math.min(100, personnelCount * 15), // ~7 = 100%
      missingElements: personnelCount < 5
        ? ['Add more key personnel profiles']
        : []
    });

    // Capabilities
    const capCount = library.evidenceByType.capability.length;
    completeness.push({
      section: 'Capabilities',
      completeness: Math.min(100, capCount * 12), // ~8 = 100%
      missingElements: capCount < 5
        ? ['Document more capabilities']
        : []
    });

    // Documents
    const docCount = library.evidenceByType.document.length;
    completeness.push({
      section: 'Supporting Documents',
      completeness: Math.min(100, docCount * 25), // 4 = 100%
      missingElements: docCount < 2
        ? ['Upload capability statements or other supporting documents']
        : []
    });

    // Certifications
    const certCount = library.evidenceByType.certification.length;
    completeness.push({
      section: 'Certifications',
      completeness: Math.min(100, certCount * 20), // 5 = 100%
      missingElements: certCount < 3
        ? ['Add relevant certifications']
        : []
    });

    return completeness;
  }

  /**
   * Calculate win probability based on various factors
   */
  private calculateWinProbability(
    matchingResult: MatchingResult,
    criticalGapsCount: number,
    highRelevanceCount: number
  ): number {
    // Base probability from requirement coverage
    let probability = matchingResult.overallReadiness * 0.8;

    // Penalty for critical gaps (each critical gap reduces probability significantly)
    probability -= criticalGapsCount * 15;

    // Bonus for high relevance evidence
    probability += Math.min(20, highRelevanceCount * 3);

    // Bonus for partial matches (better than nothing)
    probability += matchingResult.partialMatches * 2;

    // Ensure within bounds
    return Math.max(0, Math.min(100, Math.round(probability)));
  }

  /**
   * Determine competitive position based on factors
   */
  private determineCompetitivePosition(
    overallReadiness: number,
    criticalGapsCount: number,
    strengthsCount: number
  ): 'STRONG' | 'MODERATE' | 'WEAK' | 'NOT_COMPETITIVE' {
    // Critical gaps are disqualifying
    if (criticalGapsCount >= 3) return 'NOT_COMPETITIVE';
    if (criticalGapsCount >= 2) return 'WEAK';

    // Use readiness and strengths
    if (overallReadiness >= 75 && strengthsCount >= 5 && criticalGapsCount === 0) {
      return 'STRONG';
    }
    if (overallReadiness >= 50 && strengthsCount >= 3) {
      return 'MODERATE';
    }
    if (overallReadiness >= 30) {
      return 'WEAK';
    }

    return 'NOT_COMPETITIVE';
  }

  /**
   * Identify strength areas from matching results
   */
  private identifyStrengthAreas(
    matchingResult: MatchingResult,
    library: ContractScoredLibrary
  ): string[] {
    const strengths: string[] = [];

    // Categories with high match rates
    const categoryMatches: Record<string, { full: number; total: number }> = {};
    for (const match of (matchingResult?.matches || [])) {
      const cat = match.requirementCategory;
      if (!categoryMatches[cat]) {
        categoryMatches[cat] = { full: 0, total: 0 };
      }
      categoryMatches[cat].total++;
      if (match.matchStatus === 'FULL') {
        categoryMatches[cat].full++;
      }
    }

    for (const [category, counts] of Object.entries(categoryMatches)) {
      if (counts.total > 0 && counts.full / counts.total >= 0.7) {
        strengths.push(`Strong ${category.replace('_', ' ')} evidence`);
      }
    }

    // High relevance evidence
    if (library.summary.highRelevanceCount >= 5) {
      strengths.push('Highly relevant experience');
    }

    // Many inferred capabilities
    if (library.summary.uniqueCapabilities.length >= 20) {
      strengths.push('Broad capability coverage');
    }

    return strengths;
  }

  /**
   * Identify weakness areas from matching results
   */
  private identifyWeaknessAreas(
    matchingResult: MatchingResult,
    gapReport: GapReport
  ): string[] {
    const weaknesses: string[] = [];

    // Categories with low match rates
    const categoryMatches: Record<string, { none: number; total: number }> = {};
    for (const match of (matchingResult?.matches || [])) {
      const cat = match.requirementCategory;
      if (!categoryMatches[cat]) {
        categoryMatches[cat] = { none: 0, total: 0 };
      }
      categoryMatches[cat].total++;
      if (match.matchStatus === 'NONE') {
        categoryMatches[cat].none++;
      }
    }

    for (const [category, counts] of Object.entries(categoryMatches)) {
      if (counts.total > 0 && counts.none / counts.total >= 0.5) {
        weaknesses.push(`Limited ${category.replace('_', ' ')} evidence`);
      }
    }

    // Profile completeness issues
    for (const section of (gapReport?.profileCompleteness || [])) {
      if (section.completeness < 50) {
        weaknesses.push(`Incomplete ${section.section.toLowerCase()}`);
      }
    }

    // Critical gaps
    const criticalGapsCount = (gapReport?.criticalGaps || []).length;
    if (criticalGapsCount > 0) {
      weaknesses.push(`${criticalGapsCount} critical compliance gaps`);
    }

    return weaknesses;
  }

  /**
   * Generate profile action string
   */
  private generateProfileAction(category: string, description: string): string {
    const sectionMap: Record<string, string> = {
      technical: 'Profile → Capabilities',
      management: 'Profile → Key Personnel',
      past_performance: 'Profile → Past Performance',
      personnel: 'Profile → Key Personnel',
      pricing: 'Profile → Pricing',
      compliance: 'Profile → Certifications',
      other: 'Profile → Company Info'
    };

    const section = sectionMap[category] || 'Profile';
    return `Go to ${section} → Add relevant information`;
  }

  /**
   * Deduplicate actions by action text
   */
  private deduplicateActions(actions: ImprovementAction[]): ImprovementAction[] {
    const seen = new Set<string>();
    return actions.filter(action => {
      const key = action.action.substring(0, 50);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
}

// Export singleton instance
export const gapAnalyzer = new GapAnalyzer();

// Export convenience functions
export function analyzeGaps(
  matchingResult: MatchingResult,
  library: ContractScoredLibrary
): GapReport {
  return gapAnalyzer.analyzeGaps(matchingResult, library);
}

export function generateWinAssessment(
  matchingResult: MatchingResult,
  library: ContractScoredLibrary,
  gapReport: GapReport
): WinAssessment {
  return gapAnalyzer.generateWinAssessment(matchingResult, library, gapReport);
}

export function generateImprovementRoadmap(
  gapReport: GapReport,
  matchingResult: MatchingResult
): ImprovementRoadmap {
  return gapAnalyzer.generateImprovementRoadmap(gapReport, matchingResult);
}
